import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Home, X, RefreshCw, Globe, Check } from "lucide-react";

const empty = {
  reference: "",
  title: "",
  listing_type: "vendita",
  address: "",
  city: "",
  neighborhood: "",
  price: "",
  price_label: "",
  surface_sqm: "",
  rooms: "",
  bathrooms: "",
  floor: "",
  energy_class: "",
  description: "",
  photos_text: "",
  immobiliare_url: "",
  website_url: "",
  private_notes: "",
  active: true,
};

function toForm(p) {
  return {
    ...empty,
    ...p,
    photos_text: (p.photos || []).join("\n"),
    price: p.price ?? "",
    surface_sqm: p.surface_sqm ?? "",
    rooms: p.rooms ?? "",
    bathrooms: p.bathrooms ?? "",
  };
}

function toPayload(f) {
  const photos = (f.photos_text || "")
    .split(/\n|,|;/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const out = { ...f };
  delete out.photos_text;
  out.photos = photos;
  ["price", "surface_sqm", "rooms", "bathrooms"].forEach((k) => {
    out[k] = out[k] === "" || out[k] == null ? null : Number(out[k]);
    if (Number.isNaN(out[k])) out[k] = null;
  });
  return out;
}

export default function Properties() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | object
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [editingUrl, setEditingUrl] = useState(false);
  const [draftUrl, setDraftUrl] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/properties");
      setList(data);
    } catch (err) {
      console.error("Properties: failed to load /properties", err?.message || err);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      setSiteUrl(data?.website_url || "");
      setDraftUrl(data?.website_url || "");
    } catch (err) {
      console.error("Properties: failed to load /settings", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    loadSettings();
  }, []);

  const saveSiteUrl = async () => {
    try {
      await api.put("/settings", { website_url: draftUrl.trim() });
      setSiteUrl(draftUrl.trim());
      setEditingUrl(false);
      toast.success("URL sito salvato");
    } catch (err) {
      console.error("Properties: failed to save website_url", err?.message || err);
      toast.error("Errore salvataggio");
    }
  };

  const startCreate = () => {
    setEditing(toForm({}));
    setShowForm(true);
  };

  const startEdit = (p) => {
    setEditing(toForm(p));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const payload = toPayload(editing);
    try {
      if (editing.id) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success("Immobile aggiornato");
      } else {
        await api.post("/properties", payload);
        toast.success("Immobile aggiunto");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo immobile?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Immobile rimosso");
    load();
  };

  const syncFromWebsite = async () => {
    if (!siteUrl) {
      toast.error("Inserisci prima l'URL del tuo sito (campo sopra il pulsante)");
      setEditingUrl(true);
      return;
    }
    setSyncing(true);
    const t = toast.loading("Sincronizzazione dal sito in corso… (10-30 secondi)");
    try {
      const { data } = await api.post("/properties/sync-website", {});
      toast.dismiss(t);
      if (data.ok) {
        toast.success(
          `Sincronizzazione completata: ${data.created} nuovi, ${data.updated} aggiornati${
            data.deactivated > 0 ? `, ${data.deactivated} disattivati (non più sul sito)` : ""
          }`,
          { duration: 6000 },
        );
        if (data.errors?.length) {
          console.warn("Sync warnings:", data.errors);
        }
        load();
      } else {
        toast.error(data.errors?.[0] || "Sincronizzazione fallita");
      }
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const onFile = async (e) => {    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const t = toast.loading("Importazione CSV in corso…");
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/properties/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(t);
      toast.success(`Importati: ${data.created} nuovi, ${data.updated} aggiornati`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} righe scartate (vedi console)`);
        console.warn("CSV errors:", data.errors);
      }
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Errore import");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono">Catalogo</div>
          <h1 className="font-display text-4xl mt-2 font-light">Immobili</h1>
          <p className="text-sm text-[#5C5B5A] mt-2 max-w-2xl">
            Quando un cliente cita un riferimento, un indirizzo o una zona di questi immobili,
            la Segretaria userà i dati per rispondere con precisione e allegare foto e link.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="properties-sync-website-button"
            onClick={syncFromWebsite}
            disabled={syncing}
            variant="outline"
            className="border-[#C85A32] text-[#C85A32] hover:bg-[#FDF1EB] font-mono uppercase text-xs tracking-[0.2em]"
          >
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizzo…" : "Sincronizza da sito"}
          </Button>
          <label className="inline-flex items-center gap-2 border border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em] px-6 py-2 cursor-pointer">
            <Upload size={14} />
            {importing ? "Import…" : "Importa CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              disabled={importing}
              data-testid="properties-import-input"
              className="hidden"
            />
          </label>
          <Button
            data-testid="properties-add-button"
            onClick={startCreate}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
          >
            <Plus size={14} className="mr-2" /> Aggiungi immobile
          </Button>
        </div>
      </div>

      {/* WEBSITE SYNC SOURCE BAR */}
      <div className="border border-[#E5E4E0] bg-white p-4 flex items-center gap-3 flex-wrap" data-testid="website-url-bar">
        <Globe size={16} className="text-[#2C4C3B] shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="label-mono text-[10px]">Sito da sincronizzare</div>
          {editingUrl ? (
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="website-url-input"
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                placeholder="https://www.abhomevenezia.com"
                className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono text-xs h-9"
                autoFocus
              />
              <button
                type="button"
                onClick={saveSiteUrl}
                data-testid="website-url-save"
                className="p-2 bg-[#2C4C3B] text-white hover:bg-[#1E3629]"
                title="Salva"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => { setEditingUrl(false); setDraftUrl(siteUrl); }}
                className="p-2 border border-[#E5E4E0] text-[#5C5B5A] hover:bg-[#F0EFEA]"
                title="Annulla"
              >
                <X size={14} />
              </button>
            </div>
          ) : siteUrl ? (
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="website-url-display"
              className="font-mono text-sm text-[#2C4C3B] underline underline-offset-4 hover:text-[#1E3629] break-all"
            >
              {siteUrl}
            </a>
          ) : (
            <span className="text-sm text-[#8C8B8A]">Nessun sito configurato</span>
          )}
        </div>
        {!editingUrl && (
          <button
            type="button"
            onClick={() => setEditingUrl(true)}
            data-testid="website-url-edit"
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] border border-[#E5E4E0] text-[#5C5B5A] hover:bg-[#F0EFEA]"
          >
            {siteUrl ? "Cambia" : "Aggiungi URL"}
          </button>
        )}
      </div>

      {showForm && editing && (
        <PropertyForm
          state={editing}
          onChange={setEditing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={save}
        />
      )}

      {/* GRID */}
      {list.length === 0 ? (
        <div className="border border-dashed border-[#E5E4E0] p-12 text-center" data-testid="properties-empty">
          <Home className="mx-auto text-[#8C8B8A]" size={32} strokeWidth={1} />
          <p className="text-sm text-[#8C8B8A] mt-3">
            Nessun immobile in catalogo. Aggiungi il primo o carica un CSV esportato da Gestim.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <article key={p.id} data-testid={`property-card-${p.id}`} className="border border-[#E5E4E0] bg-white p-5 flex flex-col fade-up">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="label-mono text-[10px] flex items-center gap-2">
                    {p.reference && <span className="text-[#2C4C3B]">RIF. {p.reference}</span>}
                    <span className="text-[#8C8B8A]">·</span>
                    <span>{p.listing_type}</span>
                  </div>
                  <h3 className="font-display text-xl mt-1 truncate">{p.title}</h3>
                </div>
                {!p.active && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-[#F0EFEA] text-[#5C5B5A]">non attivo</span>
                )}
              </div>
              <div className="text-xs text-[#5C5B5A] mt-2 font-mono">
                {[p.address, p.neighborhood, p.city].filter(Boolean).join(", ")}
              </div>
              <div className="mt-3 font-display text-2xl text-[#2C4C3B]">
                {p.price_label || (p.price ? "€ " + p.price.toLocaleString("it-IT") : "—")}
              </div>
              <div className="text-xs text-[#5C5B5A] mt-1">
                {[
                  p.surface_sqm && `${p.surface_sqm} mq`,
                  p.rooms && `${p.rooms} locali`,
                  p.bathrooms && `${p.bathrooms} bagni`,
                  p.floor && `piano ${p.floor}`,
                  p.energy_class && `cl. ${p.energy_class}`,
                ].filter(Boolean).join(" · ")}
              </div>
              {p.photos?.length > 0 && (
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8C8B8A] mt-3">
                  {p.photos.length} foto
                </div>
              )}
              <div className="flex gap-2 mt-auto pt-4">
                <Button
                  data-testid={`property-edit-${p.id}`}
                  onClick={() => startEdit(p)}
                  variant="outline"
                  className="flex-1 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
                >
                  <Pencil size={12} className="mr-1" /> Modifica
                </Button>
                <Button
                  data-testid={`property-delete-${p.id}`}
                  onClick={() => remove(p.id)}
                  variant="outline"
                  className="border-[#C85A32] text-[#C85A32] hover:bg-[#FDF1EB] font-mono uppercase text-xs tracking-[0.2em]"
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function PropertyForm({ state, onChange, onCancel, onSubmit }) {
  const upd = (k) => (e) => onChange({ ...state, [k]: e?.target ? e.target.value : e });

  return (
    <form onSubmit={onSubmit} className="border border-[#E5E4E0] bg-white p-6 space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl font-light">
          {state.id ? "Modifica immobile" : "Nuovo immobile"}
        </h3>
        <button type="button" onClick={onCancel} className="text-[#8C8B8A] hover:text-[#1C1B1A]" data-testid="property-form-close">
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Riferimento (codice interno)">
          <Input data-testid="prop-reference" value={state.reference} onChange={upd("reference")} placeholder="AB-1023" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Tipo contratto">
          <Select value={state.listing_type} onValueChange={(v) => onChange({ ...state, listing_type: v })}>
            <SelectTrigger className="border-[#E5E4E0]" data-testid="prop-listing-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vendita">Vendita</SelectItem>
              <SelectItem value="affitto">Affitto</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Titolo *" full>
          <Input required data-testid="prop-title" value={state.title} onChange={upd("title")} placeholder="Bilocale luminoso Cannaregio" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Indirizzo">
          <Input data-testid="prop-address" value={state.address} onChange={upd("address")} placeholder="Calle del Forno 12" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Zona / quartiere">
          <Input data-testid="prop-neighborhood" value={state.neighborhood} onChange={upd("neighborhood")} placeholder="Cannaregio" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Città">
          <Input data-testid="prop-city" value={state.city} onChange={upd("city")} placeholder="Venezia" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Prezzo (€)">
          <Input type="number" data-testid="prop-price" value={state.price} onChange={upd("price")} placeholder="285000" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Prezzo (testo libero)" full>
          <Input data-testid="prop-price-label" value={state.price_label} onChange={upd("price_label")} placeholder="€ 285.000 trattabili" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Superficie (mq)">
          <Input type="number" data-testid="prop-sqm" value={state.surface_sqm} onChange={upd("surface_sqm")} placeholder="65" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Locali">
          <Input type="number" data-testid="prop-rooms" value={state.rooms} onChange={upd("rooms")} placeholder="2" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Bagni">
          <Input type="number" data-testid="prop-baths" value={state.bathrooms} onChange={upd("bathrooms")} placeholder="1" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Piano">
          <Input data-testid="prop-floor" value={state.floor} onChange={upd("floor")} placeholder="1 / Terra / Ultimo" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0" />
        </Field>
        <Field label="Classe energetica">
          <Input data-testid="prop-energy" value={state.energy_class} onChange={upd("energy_class")} placeholder="A1, B, C…" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono" />
        </Field>
        <Field label="Descrizione (verrà riassunta dall'AI nelle risposte)" full>
          <Textarea data-testid="prop-description" rows={4} value={state.description} onChange={upd("description")} placeholder="Splendido bilocale ristrutturato a 100m dal Ponte di Rialto, doppia esposizione, riscaldamento autonomo…" className="border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-sm" />
        </Field>
        <Field label="URL foto (uno per riga, accetta anche separato da virgole)" full>
          <Textarea data-testid="prop-photos" rows={3} value={state.photos_text} onChange={upd("photos_text")} placeholder="https://...foto1.jpg&#10;https://...foto2.jpg" className="border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-xs" />
        </Field>
        <Field label="Link annuncio immobiliare.it">
          <Input data-testid="prop-imm-url" value={state.immobiliare_url || ""} onChange={upd("immobiliare_url")} placeholder="https://www.immobiliare.it/annuncio/123" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono text-xs" />
        </Field>
        <Field label="Link sul sito personale">
          <Input data-testid="prop-site-url" value={state.website_url || ""} onChange={upd("website_url")} placeholder="https://abhomevenezia.com/immobili/123" className="border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono text-xs" />
        </Field>
        <Field label="Note riservate (uso interno, mai citate al cliente)" full>
          <Textarea data-testid="prop-notes" rows={3} value={state.private_notes} onChange={upd("private_notes")} placeholder="Trattabile fino a 270k. Proprietario disponibile sabato mattina. Spese condominiali 80€/mese." className="border-[#E5E4E0] bg-white focus:border-[#2C4C3B] focus:ring-0 font-mono text-sm" />
        </Field>
        <div className="md:col-span-2 flex items-center gap-3 pt-2 border-t border-[#E5E4E0]">
          <Switch
            data-testid="prop-active"
            checked={!!state.active}
            onCheckedChange={(v) => onChange({ ...state, active: v })}
            className="data-[state=checked]:bg-[#2C4C3B]"
          />
          <span className="text-sm">Immobile attivo (verrà usato dalla Segretaria nelle risposte)</span>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E4E0]">
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#E5E4E0] text-[#5C5B5A] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]">
          Annulla
        </Button>
        <Button type="submit" data-testid="property-save-button" className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]">
          {state.id ? "Salva modifiche" : "Aggiungi immobile"}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="label-mono mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
